"use client";

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
  useMemo,
} from "react";
import { Autocomplete, CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import { CustomTextField } from "../formsComponents";
import { api } from "../../api/api";
import { IWhatsAppTemplate } from "../../redux/whatsappTemplate/whatsappTemplate.types";
// import { useBillingCompanyContext } from "../../providers";
import { formatText } from "../../helpers";

const initialTemplate: IWhatsAppTemplate = {
  company_outlet_uuid: "",
  name: null,
  status: null,
  reason: null,
  header: null,
  components: [],
  body: null,
  template_id: null,
};

interface IQuickReplyTemplatesAutoSearch {
  value?: string;
  disabled?: boolean;
  label?: string;
  template_name?: string;
  onSelect?: (item: IWhatsAppTemplate, inputValue: string) => void;
}

export const QuickReplyTemplatesAutoSearch = forwardRef<
  {
    clear: () => void;
  },
  IQuickReplyTemplatesAutoSearch
>((props, ref) => {
  const { value, disabled, label, template_name, onSelect } = props;
  const [options, setOptions] = useState<readonly IWhatsAppTemplate[]>([]);
  const [allOptions, setAllOptions] = useState<readonly IWhatsAppTemplate[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  // const companyOutlet = useBillingCompanyContext();
  const [search, setSearchText] = useState<string>("");
  const hasLoadedInitialData = useRef(false);
  const [key, setKey] = React.useState(0);

  const fetchAllTemplates = async () => {
    if (hasLoadedInitialData.current) return; // Only load once

    setLoading(true);
    try {
      // const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
      const url = `/conversation/get-quick-reply`;

      const res = await api.get(url);
      const data: IWhatsAppTemplate[] = res.data.data;
      setAllOptions(data);
      setOptions(data);
      hasLoadedInitialData.current = true;
    } catch (error) {
      console.error("Error fetching WhatsApp templates:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filtered WhatsApp templates
  const fetchSuggestion = useCallback(
    async (enteredValue: string) => {
      setLoading(true);
      try {
        let searchQuery = "";
        if (enteredValue.length > 0) {
          searchQuery += `&name=${enteredValue}`;
        }
        // const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
        const url = `/conversation/get-quick-reply?pageNo=1&pageLimit=100&itemPerPage=100${searchQuery}`;

        const res = await api.get(url);
        const data: IWhatsAppTemplate[] = res.data.data;
        setOptions(data);
      } catch (error) {
        console.error("Error fetching WhatsApp templates:", error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

   
  const debounceFn = useMemo(
    () => debounce(fetchSuggestion, 800),
    [fetchSuggestion]
  );

  const getOptionLabel = (option: string | IWhatsAppTemplate) => {
    if (typeof option === "string") {
      return formatText(option);
    }
    if (option.name) {
      return formatText(option.name);
    }
    return formatText(option.template_id || "");
  };

  // Handle search text changes
  useEffect(() => {
    if (search && search !== value) {
      debounceFn(search);
    } else if (!search && hasLoadedInitialData.current) {
      // If search is cleared, show all options
      setOptions(allOptions);
    }
  }, [search, value, debounceFn, allOptions]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText("");
      setKey((prev) => prev + 1);
      hasLoadedInitialData.current = false;
      setOptions([]);
      setAllOptions([]);
      onSelect?.(initialTemplate, "");
    },
  }));

  return (
    <>
      <Autocomplete
        id="whatsapp-templates-auto-search"
        fullWidth
        key={key}
        freeSolo
        sx={{
          ".MuiOutlinedInput-root": {
            paddingTop: "2px",
            paddingBottom: "2px",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
            width: "100%",
          },
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) =>
          typeof option === "string"
            ? option === value
            : typeof option !== "string" &&
              typeof value !== "string" &&
              option.name === value.name
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        value={
          options.find((option) => option.name === value) ||
          template_name ||
          null
        }
        loading={loading}
        disabled={disabled}
        noOptionsText={
          hasLoadedInitialData.current
            ? "No WhatsApp templates found"
            : "Click to load templates"
        }
        onChange={(event, value: string | IWhatsAppTemplate | null) => {
          if (typeof value === "object" && value !== null) {
            onSelect?.(value, "");
          } else {
            onSelect?.(initialTemplate, "");
          }
        }}
        onInputChange={(event, newInputValue) => {
          if ((event && event.type === "change") || !newInputValue) {
            setSearchText(newInputValue);
            onSelect?.(initialTemplate, newInputValue);
          }
        }}
        onFocus={() => {
          // Load all options when user clicks on the field
          if (!hasLoadedInitialData.current) {
            fetchAllTemplates();
          }
        }}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label={label}
            fullWidth
            placeholder="Search WhatsApp templates"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </>
  );
});
